import { normalizeExplorerPath } from '@/lib/explorer-tree-focus'

/** Per-folder override in browse tree (mutually exclusive). */
export type FolderBrowseMode = 'default' | 'always' | 'hidden'

export const BROWSE_INACTIVE_DAY_OPTIONS = [30, 60, 90, 180] as const
export type BrowseInactiveDays = (typeof BROWSE_INACTIVE_DAY_OPTIONS)[number]

export const BROWSE_FOCUS_DEFAULTS = {
  focusBrowse: true,
  inactiveDays: 90 as BrowseInactiveDays,
  showActivityInTree: false,
  focusMinChildren: 20,
  focusMaxVisible: 20,
} as const

export type FolderBrowseVisibility = 'visible' | 'hidden-auto' | 'hidden-manual'

export type BrowseFolderPrefs = {
  alwaysShowPaths: string[]
  hiddenManualPaths: string[]
}

export type BrowseFocusSettings = {
  focusBrowse: boolean
  inactiveDays: number
  focusMinChildren: number
  focusMaxVisible: number
  prefs: BrowseFolderPrefs
}

export type FolderVisibilityContext = BrowseFocusSettings & {
  activityAtMs: (folderPath: string) => number | null
  isOnActivePath: (folderPath: string) => boolean
}

export function folderBrowseModeFromPrefs(
  folderPath: string,
  prefs: BrowseFolderPrefs
): FolderBrowseMode {
  const p = normalizeExplorerPath(folderPath)
  if (prefs.hiddenManualPaths.some((x) => normalizeExplorerPath(x) === p)) return 'hidden'
  if (prefs.alwaysShowPaths.some((x) => normalizeExplorerPath(x) === p)) return 'always'
  return 'default'
}

export function applyFolderBrowseMode(
  prefs: BrowseFolderPrefs,
  folderPath: string,
  mode: FolderBrowseMode
): BrowseFolderPrefs {
  const p = normalizeExplorerPath(folderPath)
  const strip = (list: string[]) =>
    list.filter((x) => normalizeExplorerPath(x) !== p)
  let alwaysShowPaths = strip(prefs.alwaysShowPaths)
  let hiddenManualPaths = strip(prefs.hiddenManualPaths)
  if (mode === 'always') alwaysShowPaths = [...alwaysShowPaths, p]
  if (mode === 'hidden') hiddenManualPaths = [...hiddenManualPaths, p]
  return { alwaysShowPaths, hiddenManualPaths }
}

export function isDocumentsRootMenu(folderName: string, folderPath: string): boolean {
  return folderName === 'Documents' || folderPath === '' || folderPath.endsWith('/Documents')
}

export function countFolderChildren(children: { type: string }[]): number {
  return children.filter((c) => c.type === 'folder').length
}

export function countFileChildren(children: { type: string }[]): number {
  return children.filter((c) => c.type === 'file').length
}

/** Enough children to show a focus zone (expand/collapse rail). */
export function parentQualifiesForFocusZone(
  children: { type: string }[],
  focusMinChildren: number
): boolean {
  return (
    children.length >= focusMinChildren ||
    countFolderChildren(children) >= focusMinChildren ||
    countFileChildren(children) >= focusMinChildren
  )
}

/** Focus rules apply when focus is on and parent qualifies as a focus zone. */
export function parentQualifiesForFocus(
  children: { type: string }[],
  settings: Pick<BrowseFocusSettings, 'focusBrowse' | 'focusMinChildren'>
): boolean {
  if (!settings.focusBrowse) return false
  return parentQualifiesForFocusZone(children, settings.focusMinChildren)
}

function isHiddenManualPath(folderPath: string, prefs: BrowseFolderPrefs): boolean {
  const p = normalizeExplorerPath(folderPath)
  return prefs.hiddenManualPaths.some((x) => normalizeExplorerPath(x) === p)
}

function isAlwaysShowPath(folderPath: string, prefs: BrowseFolderPrefs): boolean {
  const p = normalizeExplorerPath(folderPath)
  return prefs.alwaysShowPaths.some((x) => normalizeExplorerPath(x) === p)
}

/** Rank key for cap: prefer activity within inactiveDays, then recency (null activity sorts last). */
const BROWSE_FRESH_RANK_BOOST = 1e15

export function compareBrowseActivityRank(
  aMs: number | null,
  bMs: number | null,
  cutoff: number
): number {
  const score = (ms: number | null) => {
    const t = ms ?? 0
    const fresh = ms != null && ms >= cutoff
    return (fresh ? BROWSE_FRESH_RANK_BOOST : 0) + t
  }
  return score(bMs) - score(aMs)
}

function applyFocusCapToChildren(
  children: { path: string }[],
  map: Map<string, FolderBrowseVisibility>,
  ctx: FolderVisibilityContext,
  cutoff: number,
  isExempt: (path: string) => boolean
): void {
  const capCandidates = children
    .filter((c) => {
      const v = map.get(c.path)
      if (v === 'hidden-manual' || isExempt(c.path)) return false
      return true
    })
    .map((c) => ({
      path: c.path,
      activity: ctx.activityAtMs(c.path) ?? null,
    }))
    .sort((a, b) => compareBrowseActivityRank(a.activity, b.activity, cutoff))

  capCandidates.forEach((item, idx) => {
    map.set(item.path, idx < ctx.focusMaxVisible ? 'visible' : 'hidden-auto')
  })
}

/**
 * Resolve visibility for immediate folder children of a parent.
 * Manual hide applies whenever focus is on; auto-hide + cap only when parent qualifies.
 */
export function resolveFolderChildVisibilities(
  children: { path: string; type: string }[],
  ctx: FolderVisibilityContext
): Map<string, FolderBrowseVisibility> {
  const map = new Map<string, FolderBrowseVisibility>()
  const folderChildren = children.filter((c) => c.type === 'folder')
  const qualifies = parentQualifiesForFocus(children, ctx)
  const cutoff = Date.now() - ctx.inactiveDays * 86_400_000

  for (const c of folderChildren) {
    const path = c.path
    if (!ctx.focusBrowse) {
      map.set(path, 'visible')
      continue
    }
    if (isHiddenManualPath(path, ctx.prefs)) {
      map.set(path, 'hidden-manual')
      continue
    }
    if (isAlwaysShowPath(path, ctx.prefs) || ctx.isOnActivePath(path)) {
      map.set(path, 'visible')
      continue
    }
    if (!qualifies) {
      map.set(path, 'visible')
      continue
    }
    map.set(path, 'hidden-auto')
  }

  if (!ctx.focusBrowse || !qualifies) {
    return map
  }

  applyFocusCapToChildren(
    folderChildren,
    map,
    ctx,
    cutoff,
    (path) => isAlwaysShowPath(path, ctx.prefs) || ctx.isOnActivePath(path)
  )

  return map
}

/** @deprecated Use resolveFolderChildVisibilities for children; kept for single-path lookups. */
export function getFolderBrowseVisibility(
  folderPath: string,
  opts: {
    focusBrowse: boolean
    inactiveDays: number
    prefs: BrowseFolderPrefs
    activityAtMs: number | null
    isOnActivePath: boolean
    parentRevealed?: boolean
  }
): FolderBrowseVisibility {
  if (!opts.focusBrowse) {
    return isHiddenManualPath(folderPath, opts.prefs) ? 'hidden-manual' : 'visible'
  }
  if (isHiddenManualPath(folderPath, opts.prefs)) return 'hidden-manual'
  if (isAlwaysShowPath(folderPath, opts.prefs) || opts.isOnActivePath) return 'visible'
  if (opts.activityAtMs == null) return 'hidden-auto'
  const cutoff = Date.now() - opts.inactiveDays * 86_400_000
  if (opts.activityAtMs >= cutoff) return 'visible'
  return 'hidden-auto'
}

export function shouldRenderFolderInBrowse(
  visibility: FolderBrowseVisibility,
  focusBrowse: boolean
): boolean {
  if (!focusBrowse) return true
  return visibility === 'visible'
}

/** Build activity timestamps for each document path and folder prefixes (rollup). */
type ExplorerTreeActivityNode = {
  path: string
  type: string
  children?: ExplorerTreeActivityNode[]
  metadata?: { maxUpdatedAtMs?: number }
}

/** Walk explorer-tree nodes and collect per-folder max document updated_at (from API metadata). */
export function collectFolderMaxUpdatedFromExplorerNodes(
  nodes: ExplorerTreeActivityNode[]
): { folderPath: string; timestampMs: number }[] {
  const out: { folderPath: string; timestampMs: number }[] = []
  const walk = (list: ExplorerTreeActivityNode[]) => {
    for (const n of list) {
      if (n.type !== 'folder') continue
      const ms = n.metadata?.maxUpdatedAtMs
      if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0) {
        out.push({ folderPath: n.path, timestampMs: ms })
      }
      if (n.children?.length) walk(n.children)
    }
  }
  walk(nodes)
  return out
}

export function buildFolderActivityMap(
  entries: { filePath: string; timestampMs: number }[]
): Map<string, number> {
  const map = new Map<string, number>()
  for (const { filePath, timestampMs } of entries) {
    const norm = normalizeExplorerPath(filePath)
    map.set(norm, Math.max(map.get(norm) ?? 0, timestampMs))
    const parts = norm.split('/').filter(Boolean)
    let prefix = ''
    for (let i = 0; i < parts.length - 1; i++) {
      prefix = prefix ? `${prefix}/${parts[i]}` : parts[i]
      map.set(prefix, Math.max(map.get(prefix) ?? 0, timestampMs))
    }
    if (parts.length === 1 && parts[0]) {
      map.set(parts[0], Math.max(map.get(parts[0]) ?? 0, timestampMs))
    }
  }
  return map
}

function fileIsOnActivePath(
  filePath: string,
  activeDocPath: string | null,
  openTabPaths: string[]
): boolean {
  const p = normalizeExplorerPath(filePath)
  if (activeDocPath && normalizeExplorerPath(activeDocPath) === p) return true
  return openTabPaths.some((tab) => tab && normalizeExplorerPath(tab) === p)
}

/**
 * Resolve visibility for immediate file children (same rules as folders, per-file activity).
 */
export function resolveFileChildVisibilities(
  children: { path: string; type: string }[],
  ctx: FolderVisibilityContext & {
    activeDocPath?: string | null
    openTabPaths?: string[]
  }
): Map<string, FolderBrowseVisibility> {
  const map = new Map<string, FolderBrowseVisibility>()
  const fileChildren = children.filter((c) => c.type === 'file')
  const qualifies = parentQualifiesForFocus(children, ctx)
  const cutoff = Date.now() - ctx.inactiveDays * 86_400_000
  const activeDocPath = ctx.activeDocPath ?? null
  const openTabPaths = ctx.openTabPaths ?? []

  for (const c of fileChildren) {
    const path = c.path
    if (!ctx.focusBrowse) {
      map.set(path, 'visible')
      continue
    }
    if (isHiddenManualPath(path, ctx.prefs)) {
      map.set(path, 'hidden-manual')
      continue
    }
    if (isAlwaysShowPath(path, ctx.prefs) || fileIsOnActivePath(path, activeDocPath, openTabPaths)) {
      map.set(path, 'visible')
      continue
    }
    if (!qualifies) {
      map.set(path, 'visible')
      continue
    }
    map.set(path, 'hidden-auto')
  }

  if (!ctx.focusBrowse || !qualifies) {
    return map
  }

  applyFocusCapToChildren(
    fileChildren,
    map,
    ctx,
    cutoff,
    (path) =>
      isAlwaysShowPath(path, ctx.prefs) || fileIsOnActivePath(path, activeDocPath, openTabPaths)
  )

  return map
}

export function resolveChildVisibilities(
  children: { path: string; type: string }[],
  ctx: FolderVisibilityContext & {
    activeDocPath?: string | null
    openTabPaths?: string[]
  }
): Map<string, FolderBrowseVisibility> {
  const folderVis = resolveFolderChildVisibilities(children, ctx)
  const fileVis = resolveFileChildVisibilities(children, {
    ...ctx,
    activeDocPath: ctx.activeDocPath,
    openTabPaths: ctx.openTabPaths,
  })
  const merged = new Map<string, FolderBrowseVisibility>(folderVis)
  for (const [path, vis] of fileVis) {
    merged.set(path, vis)
  }
  return merged
}

export function pathIsAncestorOrEqual(ancestor: string, path: string): boolean {
  const a = normalizeExplorerPath(ancestor)
  const p = normalizeExplorerPath(path)
  if (!a) return true
  return p === a || p.startsWith(a + '/')
}

/** True when `child` is a direct child folder/file of `parent` (one path segment below). */
export function isImmediateChildPath(parent: string, child: string): boolean {
  const p = normalizeExplorerPath(parent)
  const c = normalizeExplorerPath(child)
  if (!p || c === p || !c.startsWith(p + '/')) return false
  const rest = c.slice(p.length + 1)
  return rest.length > 0 && !rest.includes('/')
}

/**
 * Exempt one immediate child of an expanded folder from the activity cap when the user is
 * at that folder's hub or browsing through that child (not all siblings when context is deeper).
 */
export function shouldExemptImmediateChildOfExpanded(
  expandedFolderPath: string,
  childPath: string,
  contextFolderPaths: string[],
  activeDocPath: string | null
): boolean {
  const expanded = normalizeExplorerPath(expandedFolderPath)
  const child = normalizeExplorerPath(childPath)
  if (!expanded || !isImmediateChildPath(expanded, child)) return false

  if (contextFolderPaths.some((raw) => normalizeExplorerPath(raw) === expanded)) {
    return true
  }
  for (const raw of contextFolderPaths) {
    const ctx = normalizeExplorerPath(raw)
    if (ctx && pathIsAncestorOrEqual(child, ctx)) return true
  }
  if (activeDocPath) {
    const doc = normalizeExplorerPath(activeDocPath)
    if (doc && pathIsAncestorOrEqual(child, doc)) return true
  }
  return false
}

/**
 * @deprecated Renamed to shouldExemptImmediateChildOfExpanded (per-child). Retained so a
 * partial Vite HMR update cannot throw ReferenceError before the call site reloads.
 */
export function isBrowsingInsideExpandedFolder(
  expandedFolderPath: string,
  contextFolderPaths: string[],
  activeDocPath: string | null
): boolean {
  const expanded = normalizeExplorerPath(expandedFolderPath)
  if (!expanded) return false
  if (contextFolderPaths.some((raw) => normalizeExplorerPath(raw) === expanded)) {
    return true
  }
  for (const raw of contextFolderPaths) {
    const ctx = normalizeExplorerPath(raw)
    if (ctx && pathIsAncestorOrEqual(ctx, expanded)) return true
  }
  if (activeDocPath) {
    const doc = normalizeExplorerPath(activeDocPath)
    if (doc && pathIsAncestorOrEqual(doc, expanded)) return true
  }
  return false
}

export type BrowseTreeVisibilitySession = {
  /** Sidebar-expanded folder paths — show their immediate children while expanded. */
  expandedFolderPaths?: string[]
  /** Hub/explorer folder context (e.g. selectedProjectId) when activeDocumentPath is unset. */
  contextFolderPaths?: string[]
}

export type HiddenChildrenBreakdown = {
  folders: number
  files: number
  total: number
}

export function countHiddenUnderParentBreakdown(
  children: { path: string; type: string }[],
  ctx: FolderVisibilityContext & { activeDocPath?: string | null; openTabPaths?: string[] }
): HiddenChildrenBreakdown {
  if (!ctx.focusBrowse || !parentQualifiesForFocus(children, ctx)) {
    return { folders: 0, files: 0, total: 0 }
  }
  const vis = resolveChildVisibilities(children, ctx)
  let folders = 0
  let files = 0
  for (const c of children) {
    const v = vis.get(c.path)
    if (v !== 'hidden-auto' && v !== 'hidden-manual') continue
    if (c.type === 'folder') folders++
    else if (c.type === 'file') files++
  }
  return { folders, files, total: folders + files }
}

export function countHiddenUnderParent(
  children: { path: string; type: string }[],
  ctx: FolderVisibilityContext & { activeDocPath?: string | null; openTabPaths?: string[] }
): number {
  return countHiddenUnderParentBreakdown(children, ctx).total
}

/** Label for inline "show all" link under a filtered parent. */
export function formatHiddenChildrenLabel(breakdown: HiddenChildrenBreakdown): string {
  const { folders, files, total } = breakdown
  if (total === 0) return 'Show hidden'
  if (folders > 0 && files > 0) return `Show ${total} hidden items`
  if (folders > 0) return `Show ${folders} hidden folders`
  return `Show ${files} hidden files`
}

/** Sort siblings by browse activity (most recent first) when focus rules apply. */
export function sortChildrenByBrowseActivity<T extends { path: string; type: string }>(
  children: T[],
  ctx: FolderVisibilityContext
): T[] {
  if (!ctx.focusBrowse || !parentQualifiesForFocus(children, ctx)) {
    return children
  }
  const cutoff = Date.now() - ctx.inactiveDays * 86_400_000
  return [...children].sort((a, b) => {
    const rank = compareBrowseActivityRank(
      ctx.activityAtMs(a.path),
      ctx.activityAtMs(b.path),
      cutoff
    )
    if (rank !== 0) return rank
    const aMs = ctx.activityAtMs(a.path) ?? 0
    const bMs = ctx.activityAtMs(b.path) ?? 0
    if (bMs !== aMs) return bMs - aMs
    return a.path.localeCompare(b.path)
  })
}

export function filterChildrenByBrowseFocus<T extends { path: string; type: string }>(
  children: T[] | undefined,
  ctx: FolderVisibilityContext & { activeDocPath?: string | null; openTabPaths?: string[] }
): T[] {
  if (!children?.length) return []
  const visMap = resolveChildVisibilities(children, ctx)
  const filtered = children.filter((child) => {
    const v = visMap.get(child.path) ?? 'visible'
    return shouldRenderFolderInBrowse(v, ctx.focusBrowse)
  })
  return sortChildrenByBrowseActivity(filtered, ctx)
}

/** When a parent level is expanded, still omit manual Hide rows. */
export function filterChildrenManualHideOnly<T extends { path: string }>(
  children: T[] | undefined,
  prefs: BrowseFolderPrefs
): T[] {
  if (!children?.length) return []
  return children.filter((c) => !isHiddenManualPath(c.path, prefs))
}

export function buildFolderVisibilityContext(
  settings: BrowseFocusSettings,
  activityMap: Map<string, number>,
  activeDocPath: string | null,
  openTabPaths: string[],
  treeSession?: BrowseTreeVisibilitySession
): FolderVisibilityContext & { activeDocPath: string | null; openTabPaths: string[] } {
  const contextFolderPaths = (treeSession?.contextFolderPaths ?? [])
    .map((x) => normalizeExplorerPath(x))
    .filter(Boolean)
  const expandedFolderPaths = (treeSession?.expandedFolderPaths ?? [])
    .map((x) => normalizeExplorerPath(x))
    .filter(Boolean)

  return {
    ...settings,
    activeDocPath,
    openTabPaths,
    activityAtMs: (path: string) =>
      activityMap.get(normalizeExplorerPath(path)) ?? null,
    isOnActivePath: (folderPath: string) => {
      const p = normalizeExplorerPath(folderPath)
      const pathRefs = [
        activeDocPath,
        ...contextFolderPaths,
        ...openTabPaths,
      ]
        .filter((x): x is string => Boolean(x))
        .map((x) => normalizeExplorerPath(x))

      for (const ref of pathRefs) {
        if (pathIsAncestorOrEqual(p, ref)) return true
      }

      for (const expanded of expandedFolderPaths) {
        if (
          shouldExemptImmediateChildOfExpanded(
            expanded,
            p,
            contextFolderPaths,
            activeDocPath
          )
        ) {
          return true
        }
      }

      return false
    },
  }
}
